/**
 * Lecture des refus de validation renvoyés par l'API.
 *
 * Partagée par les formulaires du dossier apprenant : ils envoient les mêmes
 * rubriques, et un refus doit se lire de la même façon d'un écran à l'autre.
 */
import { ErreurApi } from './api';

// ---------------------------------------------------------------------------
// Refus de l'API
// ---------------------------------------------------------------------------

/** Une rubrique refusée par la validation d'entrée de l'API. */
export interface DetailRefus {
  champ: string;
  message: string;
}

/**
 * Détails d'un refus de validation, tels que l'API les renvoie.
 *
 * Le corps d'erreur porte `details: [{ champ, message }]`. Le format est
 * vérifié plutôt que supposé : une erreur réseau ou une panne serveur passent
 * par le même chemin, et lire `details` à l'aveugle ferait planter la fenêtre
 * au moment précis où elle doit expliquer ce qui ne va pas.
 */
export function detailsRefus(erreur: unknown): DetailRefus[] {
  if (!(erreur instanceof ErreurApi) || !Array.isArray(erreur.details)) return [];
  return erreur.details.flatMap((detail) => {
    if (typeof detail !== 'object' || detail === null) return [];
    const { champ, message } = detail as { champ?: unknown; message?: unknown };
    if (typeof message !== 'string') return [];
    return [{ champ: typeof champ === 'string' ? champ : '', message }];
  });
}

/** Nom lisible d'une rubrique, à partir du nom technique renvoyé par l'API. */
export function libelleChamp(champ: string): string {
  return LIBELLES_CHAMPS[champ] ?? champ;
}

const LIBELLES_CHAMPS: Record<string, string> = {
  nom: 'Nom de naissance',
  prenom: 'Premier prénom',
  siteCode: 'Antenne de rattachement',
  nir: 'NIR',
  nomUsage: 'Nom d’usage',
  departementNaissance: 'Département de naissance',
  regimeSocial: 'Régime social',
  email: 'Courriel',
  telephone: 'Téléphone',
  codePostal: 'Code postal',
  ville: 'Commune',
  dateNaissance: 'Date de naissance',
  dateEntree: 'Date d’entrée prévue',
  formationCode: 'Formation visée',
  situationAvantContrat: 'Situation avant ce contrat',
  dernierDiplomePrepare: 'Dernier diplôme préparé',
  derniereClasseSuivie: 'Dernière classe suivie',
  diplomeLePlusEleve: 'Diplôme le plus élevé obtenu',
  intitulePrecisDernierDiplome: 'Intitulé du dernier diplôme',
  representantNom: 'Représentant légal — nom',
  representantPrenom: 'Représentant légal — prénom',
  representantCodePostal: 'Représentant légal — code postal',
  representantCommune: 'Représentant légal — commune',
  representantCourriel: 'Représentant légal — courriel',
};
